import { useEffect, useState } from "react";
import { Paper, TableCell, TableRow, useTheme } from "@mui/material";
import { Pencil, Trash2 } from "lucide-react";
import { FerramanetasDaListagem } from "../../shared/components/ferramentasDaListagem/ferramentas-listagem";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { formatarCpfCnpj } from "../../shared/utils";
import { ENVIRONMENT } from "../../environment/environment";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PaginacaoListagem } from "../../shared/components/ferramentasDaListagem/paginacao-listagem";
import { useDebounce } from "../../shared/utils/useDebounce/use-debounce";
import { toastError } from "../../shared/services/errors/erros-handler";
import { clienteService } from "./service/cliente.service";
import { RetornoDaListagemVazia } from "../../shared/components/retornoDaListagem/retornoDaListagem";
import { TabelaDaListagem } from "../../shared/components/tabela-da-listagem/tabelaDalistagem";
import { Badge, IconButton, Spinner } from "../../shared/components/ui";

export function ListagemClientes() {
  const [clientes, setClientes] = useState<any[]>([]);
  const [busca, setBusca] = useState("");
  const [page, setPage] = useState(0); // MUI começa em 0
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const buscaDebounce = useDebounce(busca, 500);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const reload = location.state?.reload;

  const carregarClientes = async (
    filtro?: string,
    pagina = page,
    limite = rowsPerPage
  ) => {
    try {
      const result = await clienteService.findAll(filtro, pagina, limite);
      setClientes(result.items || []);
      setTotalCount(result.totalItems || 0);
      setIsLoading(false);
    } catch (error) {
      setClientes([]);
    }
  };

  useEffect(() => {
    if (reload) {
      setIsLoading(true);
      carregarClientes(undefined, 0);
    }
  }, [reload]);

  useEffect(() => {
    setPage(0);
    carregarClientes(buscaDebounce, 0);
  }, [buscaDebounce]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    carregarClientes(buscaDebounce, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    carregarClientes(buscaDebounce, 0, newLimit);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir este cliente?")) {
      try {
        const empresaDeletada = await clienteService.deletar(id);
        toast.success(empresaDeletada);
        carregarClientes(buscaDebounce, page, rowsPerPage);
      } catch (error) {
        toastError(error);
      }
    }
  };

  return (
    <BaseLayout
      title="Listagem de Clientes"
      barraDeFerramentas={
        <FerramanetasDaListagem
          placeholder={ENVIRONMENT.INPUT_DE_BUSCA + " por nome"}
          textoBotaoNovo="Adicionar Novo"
          mostrarInputBusca
          textoDaBusca={busca}
          aoMudarTextoDeBusca={setBusca}
          aoClicarBotaoNovo={() => navigate("/cliente/criar")}
        />
      }
    >
      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none", background: "transparent" }}>
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <>
            <TabelaDaListagem
              ariaLabel="tabela de clientes"
              headTable={["Nome", "CNPJ/CPF", "Quantidades de Obras", "Ativada", "Ações"]}
              columnWidths={[180, 120, 80, 80, 100]}
              tableBody={
                clientes.length > 0 ? (
                  clientes.map((cliente) => (
                    <TableRow hover key={cliente.id} sx={{ height: 40 }}>
                      <TableCell sx={{ minWidth: 180 }}>
                        {cliente.fantasyName || cliente.fullName}
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        {cliente.cpf && cliente.cpf.trim() !== ""
                          ? formatarCpfCnpj(cliente.cpf)
                          : formatarCpfCnpj(cliente.cnpj)}
                      </TableCell>
                      <TableCell sx={{ minWidth: 80 }}>
                        {cliente.worksCount || 0}
                      </TableCell>
                      <TableCell sx={{ minWidth: 80 }}>
                        <Badge color={cliente.active ? "success" : "neutral"}>
                          {cliente.active ? "Sim" : "Não"}
                        </Badge>
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <div className="flex items-center gap-1">
                          <IconButton
                            size={32}
                            title="Editar"
                            onClick={() => navigate(`/cliente/detalhe/${cliente.id}`)}
                          >
                            <Pencil className="h-4 w-4" />
                          </IconButton>
                          <IconButton
                            size={32}
                            title="Excluir"
                            onClick={() => handleDelete(cliente.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <RetornoDaListagemVazia />
                    </TableCell>
                  </TableRow>
                )
              }
            />
            <PaginacaoListagem
              totalCount={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </BaseLayout>
  );
}
